import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import './styles/index.css'
import './styles/default.css'
import './styles/search.css'
import './styles/calendar.css'

import cn from 'clsx'
import {
  addDays,
  differenceInDays,
  endOfDay,
  format as fnsFormat,
  startOfDay,
  startOfMonth,
} from 'date-fns'
import React, { Component } from 'react'
import { Calendar, DateRangePicker as DateRange } from 'react-date-range'
import MaskedInput from 'react-maskedinput'
import InlineSVG from 'svg-inline-react'

import dayjs from '@disco/utils/dayjs'

import enhanceWithClickOutside from 'utils/enhance-with-click-outside'

import Button from 'components/ui/button'

import {
  type DateRange as DateRangeType,
  staticRanges,
} from './utils/static-ranges'

export const DATE_FNS_FORMAT = 'PPP'

type Props = {
  className?: string
  dropdownClassName?: string
  dropdownOpenClassName?: string
  showPredefinedRanges?: boolean
  onChange: (arg0: null | Date, arg1?: null | Date) => void
  onApply?: (
    arg0: Record<string, any> | null | undefined,
    arg1: Record<string, any> | null | undefined,
  ) => void
  minDate: Date
  maxDate: Date
  startDate: Date
  endDate: Date
  date?: Date
  theme: 'default' | 'search'
  applyButton?: boolean
  autoApply?: boolean
  allTimeCaption?: string
  calendarMode?: boolean
  format?: string
  disabled?: boolean
  scrollContainer?: HTMLDivElement | null | undefined
  compact?: boolean
  placeholder?: string
}
type State = {
  dropdownOpen: boolean
  startDate: Date
  endDate: Date
  popoverStyle: Record<string, any> | null | undefined
  ranges: Array<DateRangeType>
  updatingCalendarValue: boolean
}

const getPopoverStyle = (container: HTMLElement) => {
  const rect = container.getBoundingClientRect()
  const y = rect.top + rect.height + 4 // from &_Dropdown style

  return {
    position: 'fixed',
    top: `${y}px`,
    right: `${0.95}rem`,
  }
}

/**
 * Comprehensive date range picker component with calendar interface
 * Supports both single date and date range selection with predefined ranges
 * Features dropdown positioning, input masking, and auto-apply functionality
 */
class DateRangePicker extends Component<Props, State> {
  state: State = {
    dropdownOpen: false,
    startDate: this.props.startDate,
    endDate: this.props.endDate,
    updatingCalendarValue: false,
    popoverStyle: null,
    ranges: [
      {
        key: 'All time',
        startDate: this.props.maxDate || new Date(),
        endDate: this.props.minDate || new Date(),
      },
    ],
  }
  static defaultProps = {
    minDate: new Date(0),
    maxDate: undefined,
    theme: 'default',
    applyButton: true,
    allTimeCaption: 'All time',
  }
  _node: any | null | undefined = null
  containerRef = React.createRef<HTMLDivElement>()
  _handleSelect = (ranges: Record<string, DateRangeType>) => {
    //https://davidwalsh.name/flow-object-values
    const range = Object.values(ranges)[0]
    if (!range) return
    let startDate = range.startDate
    let endDate = range.endDate

    if (startDate > endDate) {
      ;[startDate, endDate] = [endDate, startDate]
    }

    this.setState({
      startDate,
      endDate,
      ranges: [range],
    })

    if (this.props.autoApply) {
      // close only when there is range selection
      if (startDate && endDate && startDate.getTime() !== endDate.getTime()) {
        this.setState({
          dropdownOpen: false,
        })

        if (this.props.onChange) {
          this.props.onChange(startDate, endDate)
        }
      }
    } else {
      if (this.props.onChange) {
        this.props.onChange(startDate, endDate)
      }
    }
  }
  _handleCalendarClick = (date: any) => {
    if (this.props.disabled) return
    this.props.onChange(date)
    this.setState({
      dropdownOpen: false,
    })
  }
  _handleDropdownToggle = () => {
    if (this.props.disabled) return
    this.setState({
      dropdownOpen: !this.state.dropdownOpen,
    })

    if (this.props.onApply && this.state.dropdownOpen) {
      const { startDate, endDate } = this.state
      this.props.onApply(startDate, endDate)
    }
  }

  handleClickOutside() {
    this.setState({
      dropdownOpen: false,
    })
  }

  getRanges(): DateRangeType[] | null {
    const { minDate, maxDate } = this.props
    if (!minDate) return null
    const today = new Date()
    const ranges: DateRangeType[] = [
      {
        key: 'This month',
        startDate: new Date(),
        endDate: (() => {
          const start = startOfMonth(new Date())
          return minDate > start ? minDate : start
        })(),
      },
      {
        key: 'Last month',
        startDate: (() => {
          const end = new Date(today.getFullYear(), today.getMonth(), 0)
          return minDate > end ? minDate : end
        })(),
        endDate: (() => {
          const start = new Date(today.getFullYear(), today.getMonth() - 1)
          return minDate > start ? minDate : start
        })(),
      },
      {
        key: 'Last 30 Days',
        startDate: new Date(),
        endDate: (() => {
          const start = addDays(new Date(), -30)
          return minDate > start ? minDate : start
        })(),
      },
      {
        key: 'Last 90 Days',
        startDate: new Date(),
        endDate: (() => {
          const start = addDays(new Date(), -90)
          return minDate > start ? minDate : start
        })(),
      },
      {
        key: 'Last Year',
        startDate: (() => {
          const end = new Date(today.getFullYear() - 1, 11, 31, 23, 59, 59)
          return minDate > end ? minDate : end
        })(),
        endDate: (() => {
          const start = new Date(today.getFullYear() - 1, 0, 1, 0, 0, 0)
          return minDate > start ? minDate : start
        })(),
      },
      {
        key: 'This Year',
        startDate: new Date(),
        endDate: (() => {
          const start = new Date(today.getFullYear(), 0, 1, 0, 0, 0)
          return minDate > start ? minDate : start
        })(),
      },
      {
        key: 'All time',
        startDate: maxDate || new Date(),
        endDate: minDate,
      },
    ]
    const incorrectRanges: string[] = []
    const lastYearDate = new Date(today.getFullYear() - 1, 11, 31, 23, 59, 59)
    if (lastYearDate < minDate) incorrectRanges.push('Last Year')

    const lastMonthDate = new Date(today.getFullYear(), today.getMonth(), 0)
    if (lastMonthDate < minDate) incorrectRanges.push('Last month')

    return ranges.filter(r => !incorrectRanges.includes(r.key))
  }

  _setDate = (value: null | Date, dropdownWasOpen: boolean) =>
    this.setState(
      {
        dropdownOpen: false,
      },
      () => {
        this.props.onChange(value)
        if (dropdownWasOpen)
          this.setState({
            dropdownOpen: true,
          })
      },
    )
  _onBlur = () => {
    const node = this._node
    if (!node || !node.mask) return
    const origValue = dayjs(this.props.date).format(this.props.format)
    node.mask.setValue(origValue)
  }
  _onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    const { dropdownOpen: dropdownWasOpen } = this.state

    if (dayjs(value, this.props.format, true).isValid()) {
      this._setDate(dayjs(value, this.props.format).toDate(), dropdownWasOpen)
    } else if (!value || value.trim().length === 0) {
      this._setDate(null, dropdownWasOpen)
    }
  }
  _containerScrollHandler = () => {
    const { containerRef } = this
    if (!containerRef.current || !this.props.scrollContainer) return null
    this.setState({
      popoverStyle: getPopoverStyle(containerRef.current),
    })
  }

  componentDidMount() {
    if (!this.props.scrollContainer) return

    if (this.containerRef.current) {
      this.setState({
        popoverStyle: getPopoverStyle(this.containerRef.current),
      })
    }

    this.props.scrollContainer.addEventListener(
      'scroll',
      this._containerScrollHandler,
    )
  }

  componentWillUnmount() {
    if (!this.props.scrollContainer) return
    this.props.scrollContainer.removeEventListener(
      'scroll',
      this._containerScrollHandler,
    )
  }

  render() {
    const {
      calendarMode,
      compact = false,
      disabled,
      format,
      placeholder,
      showPredefinedRanges,
    } = this.props
    const ranges =
      showPredefinedRanges && !calendarMode ? this.getRanges() : null
    const dropdownClassName = cn(
      this.props.dropdownClassName,
      'DateRangePicker_Dropdown',
      this.state.dropdownOpen &&
        cn(
          'DateRangePicker_Dropdown_Visible',
          this.props.dropdownOpenClassName,
        ),
    )
    let calendar: React.ReactNode = null
    let dateRange = ''

    if (this.props.calendarMode) {
      const selectedDate = this.props.date || null
      calendar = (
        <Calendar
          className={this.props.theme}
          date={selectedDate}
          onChange={this._handleCalendarClick}
        />
      )
      dateRange = this.props.date
        ? fnsFormat(this.props.date, DATE_FNS_FORMAT)
        : ''
    } else {
      const startDate = startOfDay(this.props.startDate)
      const minDate = startOfDay(this.props.minDate)
      const endDate = this.props.endDate ? endOfDay(this.props.endDate) : null
      const endDateString = endDate ? fnsFormat(endDate, DATE_FNS_FORMAT) : ''

      if (
        (startDate.getTime() === minDate.getTime() ||
          startDate.valueOf() < 0) &&
        (!endDate || endDateString === fnsFormat(new Date(), DATE_FNS_FORMAT))
      ) {
        dateRange = this.props.allTimeCaption ?? ''
      } else if (endDate && differenceInDays(endDate, startDate) === 30) {
        dateRange = 'Last 30 days'
      } else {
        dateRange =
          fnsFormat(startDate, DATE_FNS_FORMAT) + ' – ' + endDateString
      }

      calendar = (
        <DateRange
          ranges={this.state.ranges}
          inputRanges={[]}
          showSelectionPreview={true}
          moveRangeOnFirstSelection={false}
          months={2}
          direction="horizontal"
          staticRanges={staticRanges(ranges ?? [])}
          minDate={this.props.minDate}
          maxDate={this.props.maxDate}
          startDate={startDate}
          endDate={endDate || new Date()}
          onChange={this._handleSelect}
          className={this.props.theme}
          calendarFocus="backwards"
        />
      )
    }

    const { popoverStyle } = this.state
    const inputClassName = cn('DateRangePicker_Input', {
      'DateRangePicker_Input--default': compact === false,
      'DateRangePicker_Input--compact': compact === true,
    })
    return (
      <div
        className={cn(
          this.props.className,
          'DateRangePicker',
          cn({
            'DateRangePicker--Disabled': disabled,
          }),
        )}
        ref={this.containerRef}
      >
        <div
          className={cn('DateRangePicker_Select', {
            DateRangePicker_Select_Active: this.state.dropdownOpen,
          })}
          onClick={this._handleDropdownToggle}
        >
          {format ? (
            <MaskedInput
              // SLAM-1942: React 19 ref callbacks must return void, so wrap the body in braces.
              ref={n => {
                this._node = n
              }}
              mask={format.replace(/[YMD]/gi, '1')}
              placeholder={placeholder ?? format}
              className={inputClassName}
              value={
                this.props.date ? dayjs(this.props.date).format(format) : ''
              }
              onBlur={this._onBlur}
              onChange={this._onInputChange}
            />
          ) : (
            <input className={inputClassName} readOnly value={dateRange} />
          )}

          <InlineSVG
            className="DateRangePicker_Icon"
            src={require('!svg-inline!assets/icons/calendar.svg')}
            raw
          />
        </div>
        <div
          className={dropdownClassName}
          title={dateRange}
          style={popoverStyle ?? undefined}
        >
          {this.state.dropdownOpen && calendar}
          {this.props.applyButton && !this.props.calendarMode && (
            <div className="DateRangePicker_Dropdown_ActionPanel">
              <Button caption="Apply" onClick={this._handleDropdownToggle} />
            </div>
          )}
        </div>
      </div>
    )
  }
}

export default enhanceWithClickOutside(DateRangePicker) as any
